import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router';
import { api, unwrap } from '../../api/client';
import { errorMessage } from '../../api/errors';
import type { components } from '../../api/schema';
import { Badge, Button, Icon, Table, type Tone } from '../../design';
import { useToast } from '../../design/Toast';
import { fmtDate, fmtDateTime } from '../../lib/format';

export type Document = components['schemas']['Document'];
export type DocumentClassification = components['schemas']['DocumentClassification'];
export type DocumentLimits = components['schemas']['DocumentLimits'];

export const CLASSIFICATION: Record<DocumentClassification, { label: string; tone: Tone }> = {
  internal: { label: 'Intern', tone: 'blue' },
  confidential: { label: 'Confidențial', tone: 'purple' },
  public: { label: 'Public', tone: 'neutral' },
};

export function fmtBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

export function useDocumentLimits() {
  return useQuery({
    queryKey: ['documents', 'limits'],
    queryFn: () => unwrap(api.GET('/documents/limits')),
    staleTime: 300_000,
  });
}

/** Descărcare prin URL presemnat (2 min): ancoră creată dinamic; `Content-Disposition` din URL forțează salvarea. */
export function useDownload() {
  const toast = useToast();
  return useMutation({
    mutationFn: (d: Document) =>
      unwrap(api.GET('/documents/{id}/download-url', { params: { path: { id: d.id } } })),
    onSuccess: (r, d) => {
      const a = document.createElement('a');
      a.href = r.url;
      a.download = d.name;
      a.rel = 'noopener';
      document.body.appendChild(a);
      a.click();
      a.remove();
    },
    onError: (e) => toast(errorMessage(e), 'error'),
  });
}

export function useDeleteDocument() {
  const qc = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationFn: (d: Document) =>
      unwrap(api.DELETE('/documents/{id}', { params: { path: { id: d.id } } })),
    onSuccess: () => {
      toast('Documentul a fost șters; fișierul dispare definitiv după 30 de zile.', 'success');
      void qc.invalidateQueries({ queryKey: ['documents'] });
      void qc.invalidateQueries({ queryKey: ['alerts'] });
    },
    onError: (e) => toast(errorMessage(e), 'error'),
  });
}

export function DocumentsTable({
  rows,
  empty,
  canEdit,
  compact,
}: {
  rows: Document[];
  empty: string;
  canEdit: boolean;
  compact?: boolean;
}) {
  const download = useDownload();
  const remove = useDeleteDocument();
  return (
    <Table<Document>
      columns={[
        {
          key: 'name',
          header: 'Document',
          render: (d) => (
            <span className="flex" style={{ alignItems: 'flex-start' }}>
              <Icon name="document" size={18} style={{ color: 'var(--blue)', flex: 'none' }} />
              <span>
                <strong>{d.name}</strong>
                <div className="small muted">
                  {fmtBytes(d.size)} · {d.uploadedBy} · {fmtDateTime(d.createdAt)}
                </div>
              </span>
            </span>
          ),
        },
        {
          key: 'class',
          header: 'Clasificare',
          render: (d) => (
            <Badge tone={CLASSIFICATION[d.classification].tone}>
              {CLASSIFICATION[d.classification].label}
            </Badge>
          ),
        },
        ...(compact
          ? []
          : [
              {
                key: 'link',
                header: 'Asociere',
                render: (d: Document) => (
                  <span className="small">
                    {d.operationId ? (
                      <Link to={`/operatiuni/${d.operationId}`}>{d.operationTitle}</Link>
                    ) : d.lockerId ? (
                      <Link to={`/lockere/${d.lockerId}`}>{d.lockerName}</Link>
                    ) : (
                      <span className="muted">—</span>
                    )}
                  </span>
                ),
              },
            ]),
        {
          key: 'retention',
          header: 'Retenție',
          render: (d) => (
            <span className="small">
              {fmtDate(d.retentionUntil)}
              {d.retentionDue && (
                <>
                  {' '}
                  <Badge tone="orange">la termen</Badge>
                </>
              )}
              {d.status === 'deleted' && (
                <>
                  {' '}
                  <Badge tone="red">șters</Badge>
                </>
              )}
            </span>
          ),
        },
        {
          key: 'actions',
          header: '',
          align: 'right',
          render: (d) =>
            d.status === 'available' ? (
              <span className="flex" style={{ justifyContent: 'flex-end' }}>
                <Button
                  small
                  onClick={() => download.mutate(d)}
                  busy={download.isPending && download.variables?.id === d.id}
                  aria-label={`Descarcă ${d.name}`}
                >
                  Descarcă
                </Button>
                {canEdit && (
                  <Button
                    small
                    variant="danger"
                    icon="trash"
                    aria-label={`Șterge ${d.name}`}
                    onClick={() => {
                      if (window.confirm(`Ștergi documentul „${d.name}”?`)) remove.mutate(d);
                    }}
                  />
                )}
              </span>
            ) : null,
        },
      ]}
      rows={rows}
      rowKey={(d) => d.id}
      empty={empty}
    />
  );
}
